import { GoalCard } from "@/pages/talent/goals/components/GoalCard.tsx";
import { useGoalSidePanel } from "@/pages/talent/goals/hooks/use-goal-side-panel.hook.ts";
import { ApiGoal } from "@/types/goals.types.ts";
import { EmptyStateBase, Skeleton, Stack, Title } from "@skailer-com/uikit";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface QuarterGroup {
  key: string;
  label: string;
  goals: ApiGoal[];
  sortOrder: number;
}

interface GoalsGroupingByQuarterProps {
  goals: ApiGoal[];
  isLoading?: boolean;
  emptyStateComponent?: ReactNode;
  reverse?: boolean;
}

function getQuarterFromDate(date: Date): { quarter: number; year: number } {
  const d = new Date(date);
  const month = d.getMonth();
  const quarter = Math.floor(month / 3) + 1;
  const year = d.getFullYear();
  return { quarter, year };
}

export function GoalsGroupingByQuarter({
  goals,
  isLoading,
  emptyStateComponent,
  reverse = false,
}: GoalsGroupingByQuarterProps) {
  const { t } = useTranslation();
  const { openGoalPanel } = useGoalSidePanel();

  const groupedGoals = useMemo<QuarterGroup[]>(() => {
    const groups: Record<string, QuarterGroup> = {};

    for (const goal of reverse ? [...goals].reverse() : goals) {
      const { quarter, year } = getQuarterFromDate(goal.date_end);
      const key = `${year}-Q${quarter}`;
      const sortOrder = year * 10 + quarter;

      if (!groups[key]) {
        groups[key] = {
          key,
          label: t("goals:quarter_format", { quarter, year }),
          goals: [],
          sortOrder,
        };
      }
      groups[key].goals.push(goal);
    }

    return Object.values(groups).sort((a, b) =>
      reverse ? b.sortOrder - a.sortOrder : a.sortOrder - b.sortOrder
    );
  }, [goals, reverse, t]);

  if (isLoading) {
    return (
      <Stack gap={"md"}>
        <Skeleton className="h-6 w-24" />
        <Stack gap={"sm"}>
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </Stack>
      </Stack>
    );
  }

  if (goals.length === 0) {
    return (
      emptyStateComponent ?? (
        <EmptyStateBase
          emoji={":dart:"}
          title={t("goals:goals_empty_state_title")}
          description={t("goals:goals_empty_state_description")}
        />
      )
    );
  }

  return (
    <Stack gap={"lg"}>
      {groupedGoals.map((group) => (
        <Stack key={group.key} gap={"sm"}>
          <Title header={group.label} paddingTop paddingBottom />
          <Stack gap={"sm"}>
            {group.goals.map((goal) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                to={`/goals/strategic/${goal.id}`}
                onInfoClick={() => {
                  openGoalPanel(goal);
                }}
              />
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}
